import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  TESTIMONY_DRAFT_KEEP_FAILED_EVENT,
  dispatchTestimonyDraftKeepFailed,
  type TestimonyDraftKeepFailedDetail,
} from '@/lib/testimony-draft-events';
import { getAuthEpoch, getAuthUserId, waitForAuthResolution } from '@/lib/keepsake/epochs';
import { getSupabase } from '@/lib/supabase';
import { setTestimonyDraftNoticeVisible } from '@/lib/top-notice-visibility';
import { useNoticeSlot } from '@/lib/app-notice-stack';
import { DEVICE_TEST_NOTICE_PREVIEW } from '@/lib/device-test-notice-preview';
import styles from './TestimonyDraftNotice.module.css';

/**
 * App-level notice that the latest changes to a testimony note could not be
 * kept (testimony plan rev 4, section 9.3). Mounted once inside the owner
 * runtime. An event is shown only when its owner id and auth epoch equal the
 * current ones at the moment it arrives, so a late failure from an earlier
 * session (including A, then B, then A again) is never shown to a later one.
 * Any owner or auth-epoch change clears what is shown. Error class, announced
 * assertively, and it stays until dismissed.
 */
export default function TestimonyDraftNotice() {
  const { t } = useTranslation();
  const [shown, setShown] = useState<TestimonyDraftKeepFailedDetail | null>(null);
  // Presentation only: rendered into the app notice stack's draft slot.
  const slot = useNoticeSlot('draft');

  useEffect(() => {
    const onFailed = (event: Event) => {
      const detail = (event as CustomEvent<TestimonyDraftKeepFailedDetail>).detail;
      if (
        !detail ||
        typeof detail.ownerId !== 'string' ||
        typeof detail.authEpoch !== 'number' ||
        detail.ownerId !== getAuthUserId() ||
        detail.authEpoch !== getAuthEpoch()
      ) return;
      setShown({ ...detail });
    };
    window.addEventListener(TESTIMONY_DRAFT_KEEP_FAILED_EVENT, onFailed);
    return () => window.removeEventListener(TESTIMONY_DRAFT_KEEP_FAILED_EVENT, onFailed);
  }, []);

  // Device-test builds only (`device-test-notice-preview.ts`): when the page was
  // opened with `?device-test-notice=draft`, show this notice for the signed-in
  // session once auth has resolved, through the same event and the same owner
  // and auth-epoch checks as a real failure. Presentation only; nothing is read
  // or written. Every other build drops this branch.
  useEffect(() => {
    if (!DEVICE_TEST_NOTICE_PREVIEW) return;
    let requested = false;
    try {
      requested = new URLSearchParams(window.location.search).get('device-test-notice') === 'draft';
    } catch {
      requested = false;
    }
    if (!requested) return;
    let cancelled = false;
    void waitForAuthResolution().then(() => {
      const ownerId = getAuthUserId();
      if (cancelled || typeof ownerId !== 'string') return;
      dispatchTestimonyDraftKeepFailed({ ownerId, authEpoch: getAuthEpoch(), failureId: 'device-test-preview' });
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Clear on any owner or auth-epoch change. The epoch module advances inside
  // its own auth listener, so this check runs after it, and a short interval
  // covers any change this listener cannot see.
  useEffect(() => {
    if (!shown) return;
    const check = () => {
      if (shown.ownerId !== getAuthUserId() || shown.authEpoch !== getAuthEpoch()) {
        setShown(null);
      }
    };
    const subscription = getSupabase()?.auth.onAuthStateChange(() => {
      setTimeout(check, 0);
    });
    const interval = setInterval(check, 500);
    return () => {
      clearInterval(interval);
      subscription?.data.subscription.unsubscribe();
    };
  }, [shown]);

  // Read-only visibility report for the other top notices (it changes nothing here).
  useEffect(() => {
    setTestimonyDraftNoticeVisible(shown !== null);
  }, [shown]);
  useEffect(() => () => setTestimonyDraftNoticeVisible(false), []);

  if (!shown || !slot) return null;

  return createPortal(
    <div className={styles.notice} role="alert" aria-live="assertive">
      <span className={styles.message}>{t('testimonyDraft.keepFailedNotice')}</span>
      <button
        type="button"
        className={styles.dismiss}
        onClick={() => setShown(null)}
      >
        {t('testimonyDraft.keepFailedDismiss')}
      </button>
    </div>,
    slot,
  );
}
